import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as appActions from 'Actions/app';
import { MainAppReducer } from 'Types';
import { NotifyPhaserEvents, PhaserToReactListeners, RemoveListener } from 'Phaser/EventBus2';
import {
  DEFAULT_COMPLEXITY_SETTINGS,
  coerceComplexitySettings,
  complexitySettingsToTierConfig,
  type ComplexitySettings,
} from './ComplexitySettings';

/**
 * The complexity settings as they stand, and nothing else.
 *
 * Reading them is separated from owning them because the forwarding below is a
 * side effect that must happen exactly once: a second component that only wants
 * to *look* at a setting would otherwise stack another push and another answer
 * to `RequestComplexityConfig` onto the one MainAppPhaser already holds.
 *
 * Coerced on the way out as well as in: redux-persist rehydrates with
 * `hardSet`, so a slice written by an older build lands in the store without
 * passing through the reducer's own validation. Memoised on the stored object
 * so callers that depend on the identity see a stable reference.
 */
export function useComplexitySettingsValue(): ComplexitySettings {
  const stored = useSelector((state: MainAppReducer) => state.mainAppReducer.complexitySettings);
  return useMemo(() => coerceComplexitySettings(stored), [stored]);
}

/**
 * Reads the complexity settings from the store and writes changes back to it.
 *
 * The store owns them: it restores them from localStorage on load and writes
 * through on every change, so this hook holds no state of its own and several
 * mounts of the settings panel cannot disagree.
 *
 * Forwarding to Phaser happens here rather than in the reducer, which has to
 * stay pure, and splits in two:
 *
 * - a **push** when the settings change, so a controller already running picks
 *   the change up mid-session;
 * - an **answer** to `RequestComplexityConfig`, which the controller asks on
 *   construct. That is what covers the tier-change rebuild: a controller built
 *   after the last change was never pushed one and would otherwise come up on
 *   defaults.
 */
export function useComplexitySettings(): {
  settings: ComplexitySettings;
  update: (patch: Partial<ComplexitySettings>) => void;
  reset: (keys?: ReadonlyArray<keyof ComplexitySettings>) => void;
} {
  const dispatch = useDispatch();
  const settings = useComplexitySettingsValue();

  const update = useCallback(
    (patch: Partial<ComplexitySettings>) => {
      dispatch(appActions.setComplexitySettings(patch));
    },
    [dispatch],
  );

  /** Resets the named settings, or all of them when none are named. */
  const reset = useCallback(
    (keys?: ReadonlyArray<keyof ComplexitySettings>) => {
      if (!keys) {
        dispatch(appActions.setComplexitySettings({ ...DEFAULT_COMPLEXITY_SETTINGS }));
        return;
      }
      const patch: Partial<ComplexitySettings> = {};
      keys.forEach((key) => {
        // Safe: key and value are drawn from the same field of the same type.
        (patch as Record<string, unknown>)[key] = DEFAULT_COMPLEXITY_SETTINGS[key];
      });
      dispatch(appActions.setComplexitySettings(patch));
    },
    [dispatch],
  );

  const forwardConfig = useCallback(() => {
    NotifyPhaserEvents.UpdateComplexityConfig({
      config: complexitySettingsToTierConfig(settings),
    });
  }, [settings]);

  // Push: a controller already running follows a change mid-session.
  useEffect(() => {
    forwardConfig();
  }, [forwardConfig]);

  // Answer: a controller built later — a tier change rebuilds it — asks for the
  // settings itself and gets whatever is current.
  useEffect(() => {
    // Remove by reference, or each re-creation of the handler stacks another
    // listener and one request fans out to N answers.
    //
    // collectWaiting: the scene can boot and ask before this effect runs, and
    // NotifyReact only queues an event while nothing is listening. Without the
    // opt-in that queued ask is never replayed and the controller keeps its
    // defaults — the same race the push design lost.
    PhaserToReactListeners.RequestComplexityConfig(forwardConfig, undefined, false, true);
    return () => RemoveListener('RequestComplexityConfig', forwardConfig);
  }, [forwardConfig]);

  return { settings, update, reset };
}
